import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAlarm } from "@/contexts/AlarmContext";
import { usePlayer } from "@/contexts/PlayerContext";
import StationCard from "@/components/StationCard";
import VideoCard from "@/components/VideoCard";
import TrackCard from "@/components/TrackCard";
import AlarmModal from "@/components/AlarmModal";
import { TEST_IDS } from "@/constants/testIds";
import { Plus, Trash, ArrowUp, ArrowDown, ShareNetwork, Copy, Clock, Sparkle, Broadcast, MusicNotes, Alarm as AlarmIcon, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Library() {
  const { user, loading } = useAuth();
  const { playStation, playVideo, playTrack } = usePlayer();
  const { alarms, removeAlarm, toggleAlarm } = useAlarm();
  const [tab, setTab] = useState("radios");
  const [favRadios, setFavRadios] = useState([]);
  const [favVideos, setFavVideos] = useState([]);
  const [favTracks, setFavTracks] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [newName, setNewName] = useState("");
  const [openPlaylist, setOpenPlaylist] = useState(null);
  const [historyRadios, setHistoryRadios] = useState([]);
  const [historyVideos, setHistoryVideos] = useState([]);
  const [historyTracks, setHistoryTracks] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [videoRecs, setVideoRecs] = useState([]);
  const [trackRecs, setTrackRecs] = useState([]);
  const [alarmModalOpen, setAlarmModalOpen] = useState(false);
  const [alarmEditId, setAlarmEditId] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [r, v, t, p] = await Promise.all([
        api.get("/favorites/radios"),
        api.get("/favorites/videos"),
        api.get("/favorites/tracks"),
        api.get("/playlists"),
      ]);
      setFavRadios(r.data || []);
      setFavVideos(v.data || []);
      setFavTracks(t.data || []);
      setPlaylists(p.data || []);
      try {
        localStorage.setItem("recho-music.fav.radios", JSON.stringify(r.data || []));
        localStorage.setItem("recho-music.fav.videos", JSON.stringify(v.data || []));
        localStorage.setItem("recho-music.fav.tracks", JSON.stringify(t.data || []));
      } catch (_) { /* ignore */ }
    } catch (_) {
      try {
        const r = JSON.parse(localStorage.getItem("recho-music.fav.radios") || "[]");
        const v = JSON.parse(localStorage.getItem("recho-music.fav.videos") || "[]");
        const t = JSON.parse(localStorage.getItem("recho-music.fav.tracks") || "[]");
        setFavRadios(r); setFavVideos(v); setFavTracks(t);
        toast.info("Offline · showing cached favorites");
      } catch (_) { /* ignore */ }
    }
  }, [user]);

  const loadHistory = useCallback(async () => {
    if (!user) return;
    try {
      const [hr, hv, ht] = await Promise.all([
        api.get("/history/radios", { params: { limit: 24 } }),
        api.get("/history/videos", { params: { limit: 24 } }),
        api.get("/history/tracks", { params: { limit: 24 } }),
      ]);
      setHistoryRadios(hr.data || []);
      setHistoryVideos(hv.data || []);
      setHistoryTracks(ht.data || []);
    } catch (_) { /* ignore */ }
  }, [user]);

  const loadRecommendations = useCallback(async () => {
    if (!user) return;
    try {
      const [r, v, t] = await Promise.all([
        api.get("/recommendations/radios", { params: { limit: 12 } }),
        api.get("/recommendations/videos", { params: { limit: 12 } }),
        api.get("/recommendations/tracks", { params: { limit: 12 } }),
      ]);
      setRecommendations(r.data || []);
      setVideoRecs(v.data?.items || []);
      setTrackRecs(t.data?.items || []);
    } catch (_) { /* ignore */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "history") loadHistory();
    if (tab === "recommendations") loadRecommendations();
  }, [tab, loadHistory, loadRecommendations]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 font-mono text-xs uppercase tracking-[0.3em] text-[#D35230]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const removeFavRadio = async (st) => {
    await api.delete(`/favorites/radios/${st.stationuuid}`);
    setFavRadios(prev => prev.filter(x => x.stationuuid !== st.stationuuid));
  };
  const removeFavVideo = async (v) => {
    await api.delete(`/favorites/videos/${v.video_id}`);
    setFavVideos(prev => prev.filter(x => x.video_id !== v.video_id));
  };

  const createPlaylist = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/playlists", { name: newName.trim() });
      setPlaylists(prev => [data, ...prev]);
      setNewName("");
      toast.success("Playlist created");
    } catch (_) { toast.error("Failed"); }
  };

  const deletePlaylist = async (pl) => {
    await api.delete(`/playlists/${pl.id}`);
    setPlaylists(prev => prev.filter(p => p.id !== pl.id));
    if (openPlaylist?.playlist?.id === pl.id) setOpenPlaylist(null);
  };

  const openPl = async (pl) => {
    const { data } = await api.get(`/playlists/${pl.id}/videos`);
    setOpenPlaylist(data);
  };

  const toggleShare = async () => {
    if (!openPlaylist) return;
    const next = !openPlaylist.playlist.is_public;
    try {
      const { data } = await api.patch(`/playlists/${openPlaylist.playlist.id}/share`, { is_public: next });
      setOpenPlaylist(prev => ({ ...prev, playlist: data }));
      toast.success(next ? "Playlist is now public" : "Playlist is private");
    } catch (_) { toast.error("Failed"); }
  };

  const copyShareLink = async () => {
    const t = openPlaylist?.playlist?.share_token;
    if (!t) return;
    const url = `${window.location.origin}/share/playlist/${t}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch (_) {
      toast.error("Couldn't copy — " + url);
    }
  };

  const moveVideo = async (videoId, direction) => {
    if (!openPlaylist) return;
    const ids = [...(openPlaylist.playlist.video_ids || [])];
    const idx = ids.indexOf(videoId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= ids.length) return;
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    try {
      await api.post(`/playlists/${openPlaylist.playlist.id}/reorder`, { video_ids: ids });
      const orderMap = Object.fromEntries(ids.map((v, i) => [v, i]));
      setOpenPlaylist(prev => ({
        ...prev,
        playlist: { ...prev.playlist, video_ids: ids },
        videos: [...prev.videos].sort((a, b) => orderMap[a.video_id] - orderMap[b.video_id]),
      }));
    } catch (_) { toast.error("Reorder failed"); }
  };

  const clearHistory = async () => {
    if (!window.confirm("Clear all play history?")) return;
    await api.delete("/history");
    setHistoryRadios([]); setHistoryVideos([]);
    toast.success("History cleared");
  };

  const tabs = [
    { id: "radios", label: "Radios", testId: TEST_IDS.library.radios, count: favRadios.length },
    { id: "tracks", label: "Music", testId: TEST_IDS.library.tracks, count: favTracks.length },
    { id: "videos", label: "Videos", testId: TEST_IDS.library.videos, count: favVideos.length },
    { id: "playlists", label: "Playlists", testId: TEST_IDS.library.playlists, count: playlists.length },
    { id: "history", label: "History", testId: TEST_IDS.library.history },
    { id: "recommendations", label: "For You", testId: TEST_IDS.library.recommendations },
    { id: "alarms", label: "Alarms", testId: TEST_IDS.library.alarms, count: alarms.length },
  ];

  const removeFavTrack = async (t) => {
    try {
      await api.delete(`/favorites/tracks/${encodeURIComponent(t.track_id)}`);
      setFavTracks(prev => prev.filter(x => x.track_id !== t.track_id));
    } catch (_) { toast.error("Failed"); }
  };

  const DAY_LABELS = ["M","T","W","T","F","S","S"];
  const describeAlarm = (a) => {
    if (a.recurring === "daily") return "Every day";
    if (a.recurring === "weekdays") {
      const sel = (a.days || []).map((on, i) => on ? DAY_LABELS[i] : null).filter(Boolean);
      return sel.length === 0 ? "(no days)" : sel.join(" · ");
    }
    return "Once";
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 pb-32 sm:pb-40">
      <div className="mb-6 sm:mb-10 border-b border-[#1F2924]/20 pb-4 sm:pb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D35230] mb-2 sm:mb-3">Personal Atlas</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight">Your library</h1>
        <p className="text-sm sm:text-base text-[#1F2924]/70 mt-2 sm:mt-3">Saved stations, MP3 tracks, videos, playlists, history, smart picks & alarms.</p>
      </div>

      <div className="flex flex-wrap gap-1 sm:gap-2 mb-6 sm:mb-10 border-b border-[#1F2924]/20 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 scrollbar-thin">
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={t.testId}
            onClick={() => setTab(t.id)}
            className={`shrink-0 px-3 sm:px-5 py-2.5 sm:py-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] border-b-2 -mb-px ${
              tab === t.id ? "border-[#D35230] text-[#D35230]" : "border-transparent text-[#1F2924] hover:text-[#D35230]"
            }`}
          >
            {t.label}{t.count !== undefined && <span className="opacity-50"> · {t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "radios" && (
        favRadios.length === 0 ? (
          <div className="text-center py-20 font-serif-display text-2xl text-[#4A5A50]">No saved radios yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {favRadios.map(s => (
              <StationCard key={s.stationuuid} station={s} onPlay={playStation} onFav={removeFavRadio} isFav={true} />
            ))}
          </div>
        )
      )}

      {tab === "videos" && (
        favVideos.length === 0 ? (
          <div className="text-center py-20 font-serif-display text-2xl text-[#4A5A50]">No saved videos yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {favVideos.map(v => (
              <VideoCard key={v.video_id} video={v} onPlay={playVideo} onFav={removeFavVideo} isFav={true} />
            ))}
          </div>
        )
      )}

      {tab === "tracks" && (
        favTracks.length === 0 ? (
          <div className="text-center py-20 font-serif-display text-2xl text-[#4A5A50]">No saved tracks yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
            {favTracks.map(t => (
              <TrackCard key={t.track_id} track={t} onPlay={playTrack} onFav={removeFavTrack} isFav={true} />
            ))}
          </div>
        )
      )}

      {tab === "alarms" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D35230] flex items-center gap-2">
              <AlarmIcon size={14} weight="fill" /> Multi-alarm
            </div>
            <button
              data-testid="library-alarm-open-modal"
              onClick={() => setAlarmModalOpen(true)}
              className="px-4 py-2 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] flex items-center gap-2"
            >
              <Plus size={14} /> Add alarm
            </button>
          </div>
          {alarms.length === 0 ? (
            <div className="text-center py-16">
              <AlarmIcon size={48} color="#D35230" weight="duotone" className="mx-auto mb-4" />
              <p className="font-serif-display text-2xl text-[#4A5A50] mb-2">No alarms yet.</p>
              <p className="text-sm text-[#1F2924]/60">Wake to a favorite station — or schedule reminders by weekday.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {alarms.map(a => (
                <div key={a.id} data-testid="library-alarm-row" className="bg-[#EAE4D3] border-2 border-[#1F2924]/20 p-4 flex items-center gap-3">
                  <button
                    data-testid={`library-alarm-toggle-${a.id}`}
                    onClick={() => toggleAlarm(a.id)}
                    aria-label={a.enabled ? "Disable" : "Enable"}
                    className={`relative w-11 h-7 border-2 transition-colors shrink-0 ${a.enabled ? "bg-[#D35230] border-[#D35230]" : "border-[#1F2924]/40"}`}
                  >
                    <span className={`absolute top-0 ${a.enabled ? "right-0" : "left-0"} w-5 h-[22px] bg-[#F4F1EA] border border-[#1F2924]/30 transition-all`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-2xl sm:text-3xl tracking-wider text-[#1F2924] leading-none">{a.time}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D35230] mt-1">{a.label}</div>
                    <div className="font-sans text-xs text-[#4A5A50] mt-0.5 truncate">{describeAlarm(a)} · {a.station?.name}</div>
                  </div>
                  <button
                    data-testid={`library-alarm-edit-${a.id}`}
                    onClick={() => { setAlarmEditId(a.id); setAlarmModalOpen(true); }}
                    aria-label="Edit"
                    className="w-9 h-9 border border-[#1F2924]/30 hover:border-[#1F2924] flex items-center justify-center"
                  ><Pencil size={14} /></button>
                  <button
                    data-testid={`library-alarm-delete-${a.id}`}
                    onClick={() => removeAlarm(a.id)}
                    aria-label="Delete"
                    className="w-9 h-9 border border-[#1F2924]/30 hover:border-[#D35230] flex items-center justify-center"
                  ><Trash size={14} /></button>
                </div>
              ))}
            </div>
          )}
          {alarmModalOpen && <AlarmModal initialEditId={alarmEditId} onClose={() => { setAlarmModalOpen(false); setAlarmEditId(null); }} />}
        </div>
      )}

      {tab === "playlists" && (
        <div>
          <form onSubmit={createPlaylist} className="mb-8 flex items-center gap-3 max-w-md">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New playlist name…"
              className="flex-1 px-3 py-2.5 bg-transparent border-b-2 border-[#1F2924]/30 focus:border-[#D35230] outline-none font-sans"
            />
            <button
              data-testid={TEST_IDS.library.createPlaylist}
              type="submit"
              className="px-4 py-2.5 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2"
            >
              <Plus size={14} /> Create
            </button>
          </form>

          {openPlaylist ? (
            <div>
              <button onClick={() => setOpenPlaylist(null)} className="font-mono text-xs uppercase tracking-[0.2em] mb-4 hover:text-[#D35230]">← Back to playlists</button>

              <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 border-b border-[#1F2924]/20 pb-5">
                <div>
                  <h2 className="font-serif-display text-4xl mb-1">{openPlaylist.playlist.name}</h2>
                  <div className="font-mono text-xs text-[#4A5A50]">{openPlaylist.videos.length} tracks</div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    data-testid={TEST_IDS.library.shareToggle}
                    onClick={toggleShare}
                    className={`px-4 py-2 border-2 font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2 ${
                      openPlaylist.playlist.is_public
                        ? "bg-[#D35230] border-[#D35230] text-[#F4F1EA]"
                        : "border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA]"
                    }`}
                  >
                    <ShareNetwork size={14} /> {openPlaylist.playlist.is_public ? "Public" : "Make public"}
                  </button>
                  {openPlaylist.playlist.is_public && openPlaylist.playlist.share_token && (
                    <button
                      data-testid={TEST_IDS.library.copyShare}
                      onClick={copyShareLink}
                      className="px-4 py-2 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2"
                    >
                      <Copy size={14} /> Copy link
                    </button>
                  )}
                </div>
              </div>

              {openPlaylist.videos.length === 0 ? (
                <div className="font-serif-display text-xl text-[#4A5A50]">No videos in this playlist yet.</div>
              ) : (
                <div className="space-y-3">
                  {openPlaylist.videos.map((v, i) => (
                    <div key={v.video_id} className="bg-[#EAE4D3] border border-[#1F2924]/20 flex items-center gap-4 p-3">
                      <div className="font-mono text-xs text-[#4A5A50] w-8 text-center">{String(i + 1).padStart(2, "0")}</div>
                      <img src={v.thumbnail} alt="" className="w-28 h-16 object-cover border border-[#1F2924]/20" />
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-lg line-clamp-1">{v.title}</div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-[#4A5A50] truncate">{v.channel}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={TEST_IDS.library.reorderUp}
                          onClick={() => moveVideo(v.video_id, "up")}
                          disabled={i === 0}
                          className="w-8 h-8 border border-[#1F2924]/30 hover:border-[#1F2924] flex items-center justify-center disabled:opacity-30"
                          aria-label="Move up"
                        ><ArrowUp size={14} /></button>
                        <button
                          data-testid={TEST_IDS.library.reorderDown}
                          onClick={() => moveVideo(v.video_id, "down")}
                          disabled={i === openPlaylist.videos.length - 1}
                          className="w-8 h-8 border border-[#1F2924]/30 hover:border-[#1F2924] flex items-center justify-center disabled:opacity-30"
                          aria-label="Move down"
                        ><ArrowDown size={14} /></button>
                        <button
                          onClick={() => playVideo(v)}
                          className="px-3 py-2 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-[10px] uppercase tracking-[0.2em]"
                        >Play</button>
                        <button
                          onClick={async () => {
                            await api.delete(`/playlists/${openPlaylist.playlist.id}/videos/${v.video_id}`);
                            setOpenPlaylist(prev => ({
                              ...prev,
                              videos: prev.videos.filter(x => x.video_id !== v.video_id),
                              playlist: { ...prev.playlist, video_ids: (prev.playlist.video_ids || []).filter(id => id !== v.video_id) },
                            }));
                          }}
                          className="w-8 h-8 border border-[#1F2924]/30 hover:border-[#D35230] flex items-center justify-center"
                          aria-label="Remove"
                        ><Trash size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            playlists.length === 0 ? (
              <div className="text-center py-20 font-serif-display text-2xl text-[#4A5A50]">No playlists yet.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {playlists.map(pl => (
                  <div key={pl.id} className="border-2 border-[#1F2924] bg-[#EAE4D3] p-5 shadow-tactile-sm tactile-hover">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D35230] mb-2 flex items-center justify-between">
                      <span>Playlist</span>
                      {pl.is_public && <span className="text-[#3C5A41]">· Public</span>}
                    </div>
                    <h3 className="font-serif-display text-2xl mb-2 line-clamp-2">{pl.name}</h3>
                    <div className="font-mono text-xs text-[#4A5A50] mb-4">{pl.video_ids?.length || 0} tracks</div>
                    <div className="flex gap-2">
                      <button onClick={() => openPl(pl)} className="flex-1 py-2 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-[10px] uppercase tracking-[0.2em]">Open</button>
                      <button onClick={() => deletePlaylist(pl)} className="w-9 h-9 border border-[#1F2924]/30 hover:border-[#D35230] flex items-center justify-center" aria-label="Delete">
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {tab === "history" && (
        <div>
          <div className="flex items-center justify-between mb-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D35230] flex items-center gap-2">
              <Clock size={14} /> Recently tuned
            </div>
            {(historyRadios.length + historyVideos.length) > 0 && (
              <button onClick={clearHistory} className="font-mono text-xs uppercase tracking-[0.2em] hover:text-[#D35230]">
                Clear all
              </button>
            )}
          </div>

          {historyRadios.length + historyVideos.length + historyTracks.length === 0 ? (
            <div className="text-center py-20 font-serif-display text-2xl text-[#4A5A50]">
              Nothing tuned yet. Start exploring.
            </div>
          ) : (
            <div className="space-y-12">
              {historyTracks.length > 0 && (
                <div>
                  <h2 className="font-serif-display text-3xl mb-5 flex items-center gap-3">
                    <MusicNotes size={24} weight="duotone" color="#D35230" /> MP3 Tracks
                  </h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                    {historyTracks.map(h => (
                      <TrackCard
                        key={h.item_id + h.played_at}
                        track={{
                          track_id: h.item_id,
                          source: h.metadata?.source || "jamendo",
                          title: h.name,
                          artist: h.metadata?.artist || "",
                          album: h.metadata?.album || "",
                          artwork: h.metadata?.artwork || "",
                          stream_url: h.metadata?.stream_url || "",
                          duration: h.metadata?.duration || 0,
                        }}
                        onPlay={playTrack}
                        onFav={() => {}}
                        isFav={Boolean(favTracks.find(x => x.track_id === h.item_id))}
                      />
                    ))}
                  </div>
                </div>
              )}
              {historyRadios.length > 0 && (
                <div>
                  <h2 className="font-serif-display text-3xl mb-5 flex items-center gap-3">
                    <Broadcast size={24} weight="duotone" color="#D35230" /> Radios
                  </h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {historyRadios.map(h => (
                      <button
                        key={h.item_id + h.played_at}
                        onClick={() => playStation({
                          stationuuid: h.item_id, name: h.name,
                          url: h.metadata?.url, url_resolved: h.metadata?.url,
                          country: h.metadata?.country, tags: h.metadata?.tags,
                          favicon: h.metadata?.favicon,
                        })}
                        className="text-left bg-[#EAE4D3] border border-[#1F2924]/20 hover:border-[#1F2924] p-4 transition-colors"
                      >
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D35230] mb-1 truncate">
                          {h.metadata?.country || "Worldwide"}
                        </div>
                        <div className="font-serif-display text-lg leading-tight line-clamp-2 min-h-[2.75rem]">{h.name}</div>
                        <div className="font-mono text-[10px] text-[#4A5A50] mt-2 truncate">
                          {(h.metadata?.tags || "").split(",")[0] || "—"}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {historyVideos.length > 0 && (
                <div>
                  <h2 className="font-serif-display text-3xl mb-5 flex items-center gap-3">
                    <MusicNotes size={24} weight="duotone" color="#D35230" /> Videos
                  </h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {historyVideos.map(h => (
                      <button
                        key={h.item_id + h.played_at}
                        onClick={() => playVideo({
                          video_id: h.item_id, title: h.name,
                          thumbnail: h.metadata?.thumbnail, channel: h.metadata?.channel,
                        })}
                        className="text-left bg-[#EAE4D3] border border-[#1F2924]/20 hover:border-[#1F2924] transition-colors"
                      >
                        {h.metadata?.thumbnail && <img src={h.metadata.thumbnail} alt="" className="w-full aspect-video object-cover" />}
                        <div className="p-3">
                          <div className="font-serif-display text-base leading-tight line-clamp-2 min-h-[2.5rem]">{h.name}</div>
                          <div className="font-mono text-[10px] text-[#4A5A50] mt-1 truncate uppercase tracking-wider">
                            {h.metadata?.channel || "—"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "recommendations" && (
        <div className="space-y-12">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D35230] mb-2 flex items-center gap-2">
            <Sparkle size={14} weight="fill" /> Smart picks
          </div>
          <p className="font-sans text-[#1F2924]/70 -mt-10 mb-2">Curated from your listening habits.</p>

          <div>
            <h2 className="font-serif-display text-3xl mb-5 flex items-center gap-3">
              <Broadcast size={24} weight="duotone" color="#D35230" /> Radios for you
            </h2>
            {recommendations.length === 0 ? (
              <p className="font-serif-display text-xl text-[#4A5A50]">Tune in to a few stations first — we'll suggest more.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {recommendations.map(s => (
                  <StationCard
                    key={s.stationuuid}
                    station={s}
                    onPlay={playStation}
                    onFav={async (st) => {
                      if (favRadios.find(x => x.stationuuid === st.stationuuid)) return;
                      await api.post("/favorites/radios", {
                        stationuuid: st.stationuuid, name: st.name,
                        url: st.url_resolved || st.url, favicon: st.favicon || "",
                        country: st.country || "", tags: st.tags || "",
                      });
                      setFavRadios(prev => [{ stationuuid: st.stationuuid }, ...prev]);
                      toast.success("Saved");
                    }}
                    isFav={Boolean(favRadios.find(x => x.stationuuid === s.stationuuid))}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-serif-display text-3xl mb-5 flex items-center gap-3">
              <MusicNotes size={24} weight="duotone" color="#D35230" /> Videos for you
            </h2>
            {videoRecs.length === 0 ? (
              <p className="font-serif-display text-xl text-[#4A5A50]">Play a few music videos — we'll suggest similar ones.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {videoRecs.map(v => (
                  <VideoCard
                    key={v.video_id}
                    video={v}
                    onPlay={playVideo}
                    onFav={async (vv) => {
                      if (favVideos.find(x => x.video_id === vv.video_id)) return;
                      await api.post("/favorites/videos", vv);
                      setFavVideos(prev => [{ video_id: vv.video_id }, ...prev]);
                      toast.success("Saved");
                    }}
                    isFav={Boolean(favVideos.find(x => x.video_id === v.video_id))}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-serif-display text-3xl mb-5 flex items-center gap-3">
              <MusicNotes size={24} weight="duotone" color="#D35230" /> Free MP3s for you
            </h2>
            {trackRecs.length === 0 ? (
              <p className="font-serif-display text-xl text-[#4A5A50]">Play a few MP3s — we'll suggest similar tracks.</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                {trackRecs.map(t => (
                  <TrackCard
                    key={t.track_id}
                    track={t}
                    onPlay={playTrack}
                    onFav={async (tt) => {
                      if (favTracks.find(x => x.track_id === tt.track_id)) return;
                      await api.post("/favorites/tracks", tt);
                      setFavTracks(prev => [tt, ...prev]);
                      toast.success("Saved");
                    }}
                    isFav={Boolean(favTracks.find(x => x.track_id === t.track_id))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
